/* ============================================================================
   PROTON — "Frequently bought with" cart add-on rail
   ----------------------------------------------------------------------------
   Source of truth: ~/proton/public/storefront/proton-cart-addons.js
   Deployed by:     node scripts/_deploy-cart-addons.js
   Theme asset:     assets/proton-cart-addons.js

   WHAT IT DOES
     · Each card is a product FAMILY (n sibling products = n style pills).
       Picking a pill swaps product; picking the <select> swaps variant.
     · "Add" POSTs /cart/add.js asking Shopify to re-render the cart sections
       in the same round-trip (Dawn's own pattern), then swaps the returned
       HTML in. No page reload, so the shopper never loses their place.

   🚨 The section ids passed to `sections:` are the ids from templates/cart.json
      ("cart-items" / "cart-footer"), NOT the DOM ids ("main-cart-items"). They
      are read off `data-id` exactly the way assets/cart.js does it — hard-code
      either one and the re-render silently no-ops on a renamed template.

   🚨 If the cart was EMPTY when the page rendered, Dawn's cart markup carries
      `.is-empty` and an empty `.js-contents`; swapping innerHTML alone leaves
      the "your cart is empty" state on screen. That case falls back to a full
      reload on purpose.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* 🚨 Liquid already ran the `money` filter, so the presentment currency is
     baked into priceStr/compareStr. The JS fallback is USD-shaped and only
     fires if a variant somehow arrives without one. */
  function money(variant, key) {
    var pretty = variant[key + 'Str'];
    if (pretty) return pretty;
    return '$' + (variant[key] / 100).toFixed(2);
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Mirrors assets/cart.js getSectionsToRender() exactly — same four targets,
     same selectors — so a line added here re-renders identically to a quantity
     change made by the theme's own control.
     🚨 `section` is read off data-id and is the FULL scoped id
     ("template--28171129487652__cart-items"), not the short template key. */
  function sectionsToRender() {
    var out = [];
    var items = document.getElementById('main-cart-items');
    if (items && items.dataset.id) out.push({ dom: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
    var footer = document.getElementById('main-cart-footer');
    if (footer && footer.dataset.id) out.push({ dom: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
    out.push({ dom: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    if (document.getElementById('cart-live-region-text')) {
      out.push({ dom: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });
    }
    return out;
  }

  function innerOf(html, selector) {
    if (!html) return null;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector(selector);
    return el ? el.innerHTML : null;
  }

  function Card(root) {
    this.root = root;
    var raw = root.querySelector('[data-pcadd-data]');
    try {
      this.styles = JSON.parse(raw.textContent);
    } catch (e) {
      this.styles = [];
    }
    if (!this.styles.length) {
      root.remove();
      return;
    }

    this.img = root.querySelector('[data-pcadd-img]');
    this.link = root.querySelector('[data-pcadd-link]');
    this.vendor = root.querySelector('[data-pcadd-vendor]');
    this.select = root.querySelector('[data-pcadd-select]');
    this.priceEl = root.querySelector('[data-pcadd-price]');
    this.button = root.querySelector('[data-pcadd-add]');
    this.pills = Array.prototype.slice.call(root.querySelectorAll('[data-pcadd-style]'));
    this.live = root.querySelector('[data-pcadd-live]');
    this.inCart = (window.__pcaddInCart || []).map(Number);
    this.styleIndex = 0;
    this.variantIndex = 0;
    this.busy = false;

    var self = this;
    this.pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        self.selectStyle(parseInt(pill.dataset.pcaddStyle, 10));
      });
    });
    if (this.select) {
      this.select.addEventListener('change', function () {
        self.variantIndex = parseInt(self.select.value, 10) || 0;
        self.paint();
      });
    }
    if (this.button) {
      this.button.addEventListener('click', function () {
        self.add();
      });
    }

    this.selectStyle(0);
  }

  Card.prototype.style = function () {
    return this.styles[this.styleIndex];
  };

  Card.prototype.variant = function () {
    var s = this.style();
    return s && s.variants[this.variantIndex];
  };

  Card.prototype.selectStyle = function (i) {
    if (i < 0 || i >= this.styles.length) i = 0;
    this.styleIndex = i;
    var s = this.style();

    this.pills.forEach(function (pill) {
      pill.setAttribute('aria-pressed', parseInt(pill.dataset.pcaddStyle, 10) === i ? 'true' : 'false');
    });

    /* first AVAILABLE variant wins — never land the shopper on a sold-out size */
    var firstOk = 0;
    for (var v = 0; v < s.variants.length; v++) {
      if (s.variants[v].available) {
        firstOk = v;
        break;
      }
    }
    this.variantIndex = firstOk;

    if (this.select) {
      if (s.single || s.variants.length < 2) {
        this.select.hidden = true;
        this.select.innerHTML = '';
      } else {
        this.select.hidden = false;
        this.select.setAttribute('aria-label', s.optionName || 'Option');
        this.select.innerHTML = s.variants
          .map(function (variant, idx) {
            return (
              '<option value="' +
              idx +
              '"' +
              (variant.available ? '' : ' disabled') +
              (idx === firstOk ? ' selected' : '') +
              '>' +
              esc(variant.title) +
              (variant.available ? '' : ' — sold out') +
              '</option>'
            );
          })
          .join('');
      }
    }

    if (this.img && s.image) {
      this.img.src = s.image;
      this.img.alt = s.title || '';
    }
    if (this.link) {
      this.link.href = s.url;
      this.link.textContent = s.cardTitle || s.title;
    }
    if (this.vendor) this.vendor.textContent = s.vendor || '';

    this.paint();
  };

  Card.prototype.paint = function () {
    var v = this.variant();
    if (!v) return;

    if (this.priceEl) {
      var html = '';
      if (v.compare && v.compare > v.price) html += '<s>' + money(v, 'compare') + '</s>';
      html += money(v, 'price');
      this.priceEl.innerHTML = html;
    }

    var already = this.inCart.indexOf(Number(v.id)) !== -1;
    this.root.classList.toggle('is-incart', already);

    if (this.button && !this.busy) {
      this.button.classList.remove('is-added', 'is-error', 'is-loading');
      this.button.disabled = !v.available;
      this.button.textContent = !v.available ? 'Sold out' : already ? 'Add another' : 'Add to cart';
    }
  };

  Card.prototype.add = function () {
    var self = this;
    var v = this.variant();
    if (!v || !v.available || this.busy) return;

    this.busy = true;
    this.button.classList.remove('is-added', 'is-error');
    this.button.classList.add('is-loading');
    this.button.disabled = true;

    var render = sectionsToRender();
    fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({
        items: [{ id: Number(v.id), quantity: 1 }],
        sections: render
          .map(function (s) {
            return s.section;
          })
          .join(','),
        sections_url: window.location.pathname,
      }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (json) {
        if (json.status) throw new Error(json.description || json.message || 'add failed');

        self.inCart.push(Number(v.id));
        window.__pcaddInCart = self.inCart;

        var wasEmpty = document.querySelector('cart-items.is-empty, .cart.is-empty, #main-cart-footer.is-empty');
        if (wasEmpty || !json.sections) {
          window.location.reload();
          return;
        }

        render.forEach(function (s) {
          var host = document.getElementById(s.dom);
          if (!host) return;
          var html = innerOf(json.sections[s.section], s.selector);
          if (html === null) return;
          /* same fallback chain as the theme's cart.js: swap the inner of
             .js-contents when it survived, otherwise the host itself */
          var target = host.querySelector(s.selector) || host;
          target.innerHTML = html;
        });

        /* the cart is provably non-empty now — drop the empty-state classes the
           way cart.js does after a quantity change */
        var itemsEl = document.querySelector('cart-items');
        if (itemsEl) itemsEl.classList.remove('is-empty');
        var footerEl = document.getElementById('main-cart-footer');
        if (footerEl) footerEl.classList.remove('is-empty');

        /* keep the drawer/notification bubble honest for themes that cache it */
        document.dispatchEvent(new CustomEvent('proton:cart:updated', { detail: { variantId: Number(v.id) } }));

        self.busy = false;
        self.button.classList.remove('is-loading');
        self.button.classList.add('is-added');
        /* 🚨 re-enable before painting the confirmation: :disabled carries
           opacity .55, which washes the orange fill out to peach and reads as
           a greyed-out control rather than a success. */
        self.button.disabled = false;
        self.button.textContent = 'Added ✓';
        self.root.classList.add('is-incart');
        if (self.live) self.live.textContent = (self.style().cardTitle || '') + ' added to your cart.';
        window.setTimeout(function () {
          self.paint();
        }, 2200);
      })
      .catch(function () {
        self.busy = false;
        self.button.classList.remove('is-loading');
        self.button.classList.add('is-error');
        self.button.disabled = false;
        self.button.textContent = 'Try again';
      });
  };

  function boot() {
    var cards = document.querySelectorAll('[data-pcadd-card]');
    for (var i = 0; i < cards.length; i++) {
      if (!cards[i].dataset.pcaddReady) {
        cards[i].dataset.pcaddReady = '1';
        new Card(cards[i]);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
